import { Save } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import type { DeclarationCheckSettings, FailureMode } from '@/api/areas/declarations';
import { Callout, Field, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAccess } from '@/lib/access';
import { formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { FormActions } from '../compliance/ui';

const KINDS = ['integrity_failure', 'coverage_failure', 'identifier_mismatch', 'paper_rejected'] as const;
type Kind = (typeof KINDS)[number];

function SettingsForm({ initial, canEdit, onSaved }: { initial: DeclarationCheckSettings; canEdit: boolean; onSaved: () => void }) {
  const { t } = useTranslation('declarations');
  const [s, setS] = useState<DeclarationCheckSettings>(initial);
  const [reason, setReason] = useState('');
  const write = useWrite((body: DeclarationCheckSettings & { reason: string }, opts) => api.updateDeclarationCheckSettings(body, opts));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await write.run({ ...s, reason: reason.trim() })) {
      setReason('');
      onSaved();
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      {KINDS.map((k: Kind) => (
        <Field key={k} label={t(`checkSettings.kinds.${k}.label`)} hint={t(`checkSettings.kinds.${k}.hint`)}>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={s[k]}
            disabled={!canEdit || write.busy}
            onChange={(e) => setS({ ...s, [k]: e.target.value as FailureMode })}
            data-check-setting={k}
          >
            {(['STAFF_REVIEW', 'AUTO_REJECT'] as const).map((m) => (
              <option key={m} value={m}>
                {t(`checkSettings.modes.${m}`)}
              </option>
            ))}
          </select>
        </Field>
      ))}
      {canEdit ? (
        <>
          <Field label={t('read.reason')}>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} />
          </Field>
          {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
          <FormActions>
            <Button type="submit" size="sm" disabled={write.busy || !reason.trim()}>
              <Save /> {t('checkSettings.save')}
            </Button>
          </FormActions>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">{t('checkSettings.readOnly')}</p>
      )}
    </form>
  );
}

/**
 * Cài đặt › Tên miền & TLD › Kiểm tra bản khai (owner decision 2026-10-01): what a failed check
 * of a declaration upload does — staff review (default) or an automatic refusal shown to the
 * customer — one failure kind at a time. An unknown root CA is never a failure.
 */
export function DeclarationChecksPage() {
  const { t } = useTranslation('declarations');
  useDocumentTitle(t('checkSettings.title'));
  const access = useAccess();
  const result = useApi((signal) => api.getDeclarationCheckSettings({ signal }), []);
  return (
    <div className="grid gap-4">
      <PageHeader title={t('checkSettings.title')} subtitle={t('checkSettings.description')} />
      <Callout tone="info">{t('checkSettings.rootCa')}</Callout>
      <Section title={t('checkSettings.section')}>
        {result.status === 'loading' ? <Loading rows={4} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {result.status === 'success' ? (
          <div className="grid gap-3">
            {result.data.updated_at ? (
              <p className="text-xs text-muted-foreground">
                {t('checkSettings.updated', { at: formatDateTime(result.data.updated_at), by: result.data.updated_by_name ?? (result.data.updated_by ? t('checkSettings.someone') : '—') })}
              </p>
            ) : null}
            <SettingsForm key={result.data.updated_at ?? 'initial'} initial={result.data} canEdit={access.has(['compliance.override'])} onSaved={result.reload} />
          </div>
        ) : null}
      </Section>
    </div>
  );
}
